import { Loading } from "../../components/Loading";
import {
  Stack,
  Text,
  Input,
  Textarea,
  Button,
  useToast,
  Box,
  FormLabel,
  FormControl,
  Heading,
  Flex,
  Grid,
  Icon,
  SimpleGrid,
  Checkbox,
} from "@chakra-ui/react";
import { MdDragIndicator } from "react-icons/md";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RoleAccessController } from "../../components/RoleAccessController";
import { useContext, useState, useEffect } from "react";
import { getRoleById, updateRole } from "../../actions/role.actions";
import { getDashboardData } from "../../actions/dashboard.action";
import { config } from "../../configs/roles.config";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { AuthContext } from "../../providers/AuthValidationLayer";
import {
  getCanonicalOperationsSetupSubHeading,
  normalizeRoleAccessSections,
} from "../../utils/permissionAliases";
import { Unauthorized } from "../../components/Unauthorized";
import { useNavigate, useParams } from "react-router-dom";

// Droppable Column Component
const DroppableColumn = ({ id, title, subtitle, children, isEmpty }) => {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <Box flex={1}>
      <Box mb={4}>
        <Heading size="md" mb={1}>
          {title}
        </Heading>
        <Text fontSize="sm" color="gray.600">
          {subtitle}
        </Text>
      </Box>
      <Box
        ref={setNodeRef}
        minH="400px"
        p={4}
        bg={isOver ? "blue.50" : "gray.50"}
        borderRadius="xl"
        border="2px dashed"
        borderColor={isOver ? "blue.400" : "gray.300"}
        transition="all 0.2s"
      >
        {isEmpty ? (
          <Flex
            h="200px"
            align="center"
            justify="center"
            color="gray.400"
            flexDirection="column"
            gap={2}
          >
            <Text fontSize="lg">🎯</Text>
            <Text fontSize="sm">Drag cards here</Text>
          </Flex>
        ) : (
          <Stack spacing={3}>{children}</Stack>
        )}
      </Box>
    </Box>
  );
};

// Sortable Dashboard Card Component
const SortableDashboardCard = ({ id, card }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Flex
      ref={setNodeRef}
      style={style}
      bg="white"
      borderRadius="lg"
      p={4}
      border="1px solid"
      borderColor="gray.200"
      align="center"
      justify="space-between"
      cursor="grab"
      _active={{ cursor: "grabbing" }}
      _hover={{ shadow: "md", borderColor: "blue.300" }}
      {...attributes}
      {...listeners}
    >
      <Text fontWeight="medium" fontSize="md">
        {card.title}
      </Text>
      <Icon as={MdDragIndicator} w={5} h={5} color="gray.400" />
    </Flex>
  );
};

const EditRole = () => {
  const { id } = useParams();
  const { token, user, hasPermission } = useContext(AuthContext);
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [roleAccess, setRoleAccess] = useState([]);
  const [allowedAccessByType, setAllowedAccessByType] = useState({});
  const [availableCards, setAvailableCards] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState();
  const [canAccessDashboardDropdown, setCanAccessDashboardDropdown] = useState(false);
  const [canViewDashboardAs, setCanViewDashboardAs] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const [canBypassHierarchy, setCanBypassHierarchy] = useState(false);

  const canEditRoles = hasPermission("Access Control", "Roles", "PATCH");
  const isOwnRole = user?.roleId?._id === id;
  const isSystemRole = roleName?.toLowerCase() === "superadmin";
  const canEditThisRole = canEditRoles && !isOwnRole && !isSystemRole;

  const getAllowedAccessMap = () => {
    const currentUserAccess = user?.roleId?.roleAccess || [];
    const allowed = {};

    if (user?.roleId?.roleName?.toLowerCase() === "superadmin") {
      return allowed;
    }

    currentUserAccess.forEach((section) => {
      section.types?.forEach((type) => {
        allowed[type.subHeading] = type.access || [];
        const canonicalSubHeading =
          getCanonicalOperationsSetupSubHeading(type.subHeading);
        allowed[canonicalSubHeading] = type.access || [];
      });
    });

    return allowed;
  };

  const buildEditableRoleAccess = (existingRoleAccess = []) => {
    const normalizedTemplate = normalizeRoleAccessSections(config);
    const normalizedExisting = normalizeRoleAccessSections(existingRoleAccess);

    const getTypeKey = (heading, subHeading) => {
      const normalizedHeading = String(heading || "").trim().toLowerCase();
      const value =
        normalizedHeading === "operations setup"
          ? getCanonicalOperationsSetupSubHeading(subHeading)
          : String(subHeading || "").trim();

      return value.toLowerCase();
    };

    const existingByHeading = new Map(
      normalizedExisting.map((section) => [
        String(section.heading || "").toLowerCase(),
        section,
      ]),
    );

    const merged = normalizedTemplate.map((templateSection) => {
      const existingSection = existingByHeading.get(
        String(templateSection.heading || "").toLowerCase(),
      );

      if (!existingSection) {
        return {
          ...templateSection,
          types: (templateSection.types || []).map((type) => ({
            ...type,
            access: [...(type.access || [])],
          })),
        };
      }

      const existingTypeAccessMap = new Map(
        (existingSection.types || []).map((type) => [
          getTypeKey(templateSection.heading, type.subHeading),
          type.access || [],
        ]),
      );

      const templateTypeSubHeadings = new Set(
        (templateSection.types || []).map((type) =>
          getTypeKey(templateSection.heading, type.subHeading),
        ),
      );

      const extraExistingTypes = (existingSection.types || [])
        .filter(
          (type) =>
            !templateTypeSubHeadings.has(
              getTypeKey(templateSection.heading, type.subHeading),
            ),
        )
        .map((type) => ({
          ...type,
          access: existingTypeAccessMap.get(
            getTypeKey(templateSection.heading, type.subHeading),
          ) || type.access || [],
        }));

      return {
        ...templateSection,
        types: [
          ...(templateSection.types || []).map((type) => ({
            ...type,
            access:
              existingTypeAccessMap.get(
                getTypeKey(templateSection.heading, type.subHeading),
              ) || [],
          })),
          ...extraExistingTypes,
        ],
      };
    });

    const templateHeadings = new Set(
      normalizedTemplate.map((section) =>
        String(section.heading || "").toLowerCase(),
      ),
    );

    const extraExisting = normalizedExisting.filter(
      (section) =>
        !templateHeadings.has(String(section.heading || "").toLowerCase()),
    );

    return [...merged, ...extraExisting];
  };

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDashboardDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    // Find which list contains the active card
    const activeInAvailable = availableCards.find(
      (card) => card.title === activeId,
    );
    const activeInSelected = selectedCards.find(
      (card) => card.title === activeId,
    );

    // Determine source and destination containers
    const activeContainer = activeInAvailable ? "available" : "selected";
    const overContainer =
      overId === "available"
        ? "available"
        : overId === "selected"
          ? "selected"
          : selectedCards.find((card) => card.title === overId)
            ? "selected"
            : "available";

    // Moving between containers
    if (activeContainer !== overContainer) {
      const sourceCards =
        activeContainer === "available" ? availableCards : selectedCards;
      const destCards =
        overContainer === "available" ? availableCards : selectedCards;

      const activeCard = sourceCards.find((card) => card.title === activeId);
      const newSourceCards = sourceCards.filter(
        (card) => card.title !== activeId,
      );
      const newDestCards = [...destCards, activeCard];

      if (activeContainer === "available") {
        setAvailableCards(newSourceCards);
        setSelectedCards(newDestCards);
      } else {
        setSelectedCards(newSourceCards);
        setAvailableCards(newDestCards);
      }

      toast({
        title: overContainer === "selected" ? "Card Added" : "Card Removed",
        description:
          overContainer === "selected"
            ? "Card will be shown in dashboard"
            : "Card removed from dashboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    }
    // Reordering within the same container
    else if (activeId !== overId) {
      if (activeContainer === "selected") {
        setSelectedCards((items) => {
          const oldIndex = items.findIndex((item) => item.title === activeId);
          const newIndex = items.findIndex((item) => item.title === overId);
          return arrayMove(items, oldIndex, newIndex);
        });
      } else {
        setAvailableCards((items) => {
          const oldIndex = items.findIndex((item) => item.title === activeId);
          const newIndex = items.findIndex((item) => item.title === overId);
          return arrayMove(items, oldIndex, newIndex);
        });
      }
    }
  };

  const loadRole = async () => {
    setLoading(true);
    try {
      const [role, dashboardData] = await Promise.all([
        getRoleById(id),
        getDashboardData(),
      ]);

      setRoleName(role.roleName);
      setRoleDescription(role.roleDescription);
      setCanAccessDashboardDropdown(
        role?.canAccessDashboardDropdown || false
      );
      setCanViewDashboardAs(role?.canViewDashboardAs || false);
      setCanBypassHierarchy(role?.canBypassHierarchy || false);

      // Store dashboard cards - split into available and selected
      if (dashboardData?.cards) {
        // dashboardAccessKeys are stored as card.section values
        setAvailableCards(
          dashboardData.cards.filter(
            (card) => !role.dashboardAccessKeys?.includes(card.section),
          ),
        );
        setSelectedCards(
          dashboardData.cards.filter((card) =>
            role.dashboardAccessKeys?.includes(card.section),
          ),
        );

        // Map dashboard cards to roleAccess structure
        setRoleAccess(buildEditableRoleAccess(role.roleAccess));
      } else {
        setRoleAccess(buildEditableRoleAccess(role.roleAccess));
      }
    } catch (err) {
      setError(err.message);
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const onUpdateRole = async () => {
    const trimmedRoleName = roleName.trim();
    const trimmedRoleDescription = roleDescription.trim();
    const nameRegex = /^[a-zA-Z0-9 ]+$/;

    if (!trimmedRoleName || !trimmedRoleDescription) {
      toast({
        title: "Error",
        description: "Role Name or Description not provided",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!nameRegex.test(trimmedRoleName)) {
      toast({
        title: "Invalid Role Name",
        description: "Role name cannot contain special characters",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    const SelectedDashboardSections = selectedCards.map((card) => card.section);
    const payload = {
      roleName: trimmedRoleName,
      roleDescription: trimmedRoleDescription,
      roleAccess,
      dashboardAccessKeys: SelectedDashboardSections,
      canAccessDashboardDropdown,
      canViewDashboardAs,
      canBypassHierarchy
    };

    setSaving(true);

    try {
      await updateRole(id, payload, token);

      toast({
        title: "Success",
        description: "Role updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // redirect after success
      navigate("/settings/roles/manage");
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = (value, index) => {
    const newState = [...roleAccess];
    newState[index] = value;
    setRoleAccess(newState);
  };

  useEffect(() => {
    loadRole();
  }, [id]);

  useEffect(() => {
    setAllowedAccessByType(getAllowedAccessMap());
  }, [user]);

  if (isLoading) {
    return <Loading />;
  }

  if (error === "Unauthorized user" || !canEditThisRole) {
    return <Unauthorized />;
  }

  return (
    <Box px={6} py={8}>
      <BreadCrumbMaker
        items={[
          { link: "/settings", title: "Access Control" },
          { link: "/settings/roles/manage", title: "Manage Roles" },
          { link: `/settings/roles/edit/${id}`, title: "Edit Role", on: true },
        ]}
      />

      <Stack spacing={4}>
        <Box bg="white" borderRadius="2xl" p={6}>
          <Flex justifyContent="space-between">
            <Heading fontSize="lg" mb={4}>
              Role Details
            </Heading>
            <Text color="#90A3BF">Step 1 of {roleAccess.length + 2}</Text>
          </Flex>

          <FormControl isRequired mb={4}>
            <FormLabel>Role Name</FormLabel>
            <Input
              value={roleName}
              placeholder="Enter Role Name"
              w={300}
              borderRadius="lg"
              maxLength={50}
              onChange={(e) => setRoleName(e.target.value)}
              isDisabled={isSystemRole}
            />
            {!isSystemRole && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                {roleName.length}/50 characters
              </Text>
            )}
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Description</FormLabel>

            <Textarea
              value={roleDescription}
              placeholder="Role Description"
              w="50%"
              borderRadius="lg"
              resize="none"
              maxLength={255}
              onChange={(e) => setRoleDescription(e.target.value)}
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              {roleDescription.length}/255 characters
            </Text>
          </FormControl>

        </Box>

        {/* Dashboard Cards Section */}
        <Box bg="white" borderRadius="2xl" p={6}>
          <Flex justifyContent="space-between">
            <Heading fontSize="lg" mb={4}>
              Dashboard Configuration
            </Heading>
            <Text color="#90A3BF">Step 2 of {roleAccess.length + 2}</Text>
          </Flex>
          {/* Dashboard Access Permissions */}
          <Box mb={8}>

            {user?.roleId?.roleName?.toLowerCase() === "superadmin" && (
              <FormControl mb={4}>
                <Checkbox
                  isChecked={canBypassHierarchy}
                  onChange={(e) => setCanBypassHierarchy(e.target.checked)}
                >
                  <Flex flexDirection="column" gap={1}>
                    <Text fontWeight="600">Bypass Hierarchy Restrictions</Text>
                    <Text fontSize="sm" color="gray.600" fontWeight="400">
                      Allows this role to access dashboard features across all hierarchy levels
                    </Text>
                  </Flex>
                </Checkbox>
              </FormControl>
            )}

            <FormControl mb={4}>
              <Checkbox
                isChecked={canAccessDashboardDropdown}
                onChange={(e) => setCanAccessDashboardDropdown(e.target.checked)}
              >
                <Flex flexDirection="column" gap={1}>
                  <Text fontWeight="600">Access Dashboard Role Dropdown</Text>
                  <Text fontSize="sm" color="gray.600" fontWeight="400">
                    Enables this role to use the dashboard role dropdown filter feature
                  </Text>
                </Flex>
              </Checkbox>
            </FormControl>

            <FormControl>
              <Checkbox
                isChecked={canViewDashboardAs}
                onChange={(e) => setCanViewDashboardAs(e.target.checked)}
              >
                <Flex flexDirection="column" gap={1}>
                  <Text fontWeight="600">Visible in Dashboard Dropdown</Text>
                  <Text fontSize="sm" color="gray.600" fontWeight="400">
                    Makes this role appear as an option in other roles' dashboard dropdown filters
                  </Text>
                </Flex>
              </Checkbox>
            </FormControl>
          </Box>

          {!canBypassHierarchy ? (
            <>
              <Text fontSize="sm" color="gray.600" mb={6}>
                Drag cards from left to right to add them to this role's dashboard
              </Text>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDashboardDragEnd}
              >
                <Flex gap={6} direction={{ base: "column", lg: "row" }}>
                  <DroppableColumn
                    id="available"
                    title="Available Cards"
                    subtitle="Drag cards to the right to add them"
                    isEmpty={availableCards.length === 0}
                  >
                    <SortableContext
                      items={availableCards.map((card) => card.title)}
                      strategy={rectSortingStrategy}
                    >
                      {availableCards.map((card) => (
                        <SortableDashboardCard
                          key={card.title}
                          id={card.title}
                          card={card}
                        />
                      ))}
                    </SortableContext>
                  </DroppableColumn>

                  <DroppableColumn
                    id="selected"
                    title="Dashboard Cards"
                    subtitle="These cards will be shown in the dashboard"
                    isEmpty={selectedCards.length === 0}
                  >
                    <SortableContext
                      items={selectedCards.map((card) => card.title)}
                      strategy={rectSortingStrategy}
                    >
                      {selectedCards.map((card) => (
                        <SortableDashboardCard
                          key={card.title}
                          id={card.title}
                          card={card}
                        />
                      ))}
                    </SortableContext>
                  </DroppableColumn>
                </Flex>
              </DndContext>
            </>
          ) : (
            <Box p={6} bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.200">
              <Text fontSize="sm" color="blue.700">
                <Text as="span" fontWeight="600">Dashboard card configuration is not required</Text> when bypass hierarchy restrictions is enabled. This role will have access to all dashboard cards automatically.
              </Text>
            </Box>
          )}
        </Box>

        {roleAccess.map((item, index) => (
          <RoleAccessController
            key={item.heading + index}
            data={item}
            step={index + 3}
            totalSteps={roleAccess.length + 2}
            onChange={(value) => handleRoleChange(value, index)}
            allowedAccessByType={allowedAccessByType}
          />
        ))}

        <Button
          onClick={onUpdateRole}
          borderRadius="lg"
          isLoading={saving}
          loadingText="Updating..."
          colorScheme="blue"
        >
          Update Role
        </Button>
      </Stack>
    </Box>
  );
};

export default EditRole;
